import React, { useEffect } from 'react';
import { Tag, Dropdown, Space, Tooltip } from 'antd';
import { RightOutlined, MoreOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/auth';
import { FEATURE, ACTION } from '../../../helpers/permissions';
import Card from '../Card';

const ProjectCard = ({ 
  project, 
  onAction,
  onTagClick
}) => {
  const { theme } = useTheme();
  const { can } = useAuth();




  // Gerar cor aleatória baseada no ID do projeto para consistência
  const generateProjectColor = (projectId) => {
    if (!projectId) return '#722ed1';
    
    const colors = [
      '#722ed1', // Roxo principal
      '#3b82f6', // Azul
      '#10b981', // Verde
      '#f59e0b', // Amarelo/Laranja
      '#ef4444', // Vermelho
      '#8b5cf6', // Roxo claro
      '#06b6d4', // Ciano
      '#84cc16', // Verde lima
      '#f97316', // Laranja
      '#ec4899', // Rosa
      '#6366f1', // Índigo
      '#14b8a6', // Teal
      '#eab308', // Amarelo
      '#dc2626', // Vermelho escuro
      '#7c3aed'  // Violeta
    ];
    
    // Usar o ID do projeto como seed para gerar índice consistente
    let hash = 0;
    for (let i = 0; i < projectId.length; i++) {
      const char = projectId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Converter para 32bit integer
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const projectColor = generateProjectColor(project.id || project.name);

  // Menu de ações
  const getActionItems = () => {
    const items = [];
    
      if(can(FEATURE.PROJECT, [ACTION.EDIT])){
      items.push({
        key: 'settings',
        label: 'Configurações',
        onClick: () => onAction('settings', project)
      });
    }

    if (items.length > 0) {
      items.push({
        type: 'divider'
      });

      if(can(FEATURE.PROJECT, [ACTION.DELETE])){
      items.push({
        key: 'delete',
        label: 'Excluir',
        danger: true,
        onClick: () => onAction('delete', project)
      });
      }
    }

    return items;
  };

  const actionItems = getActionItems();
  const syncList = project.syncs || project.synchronizations || [];
  const hasSyncs = (Array.isArray(syncList) && syncList.length > 0) || (project.connectedErps && project.connectedErps > 0);

  return (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        border: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      styles={{
        body: {
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }
      }}
    >
      {/* Header com ícone e status */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
      }}>
        {/* Ícone/Logo do projeto */}
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          backgroundColor: project.logoUrl ? '#f8f9fa' : (projectColor || '#722ed1'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          color: project.logoUrl ? '#000' : '#FFFFFF',
          overflow: 'hidden',
          border: project.logoUrl ? '1px solid #e9ecef' : 'none'
        }}>
          {project.logoUrl && typeof project.logoUrl === 'string' && project.logoUrl.startsWith('http') ? (
            <img 
              src={project.logoUrl} 
              alt={project.name}
              style={{
                width: '70%',
                height: '70%',
                objectFit: 'cover',
                borderRadius: 11
              }}
              onError={(e) => {
                // Fallback para ícone se a imagem não carregar
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div style={{
            display: project.logoUrl ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            fontWeight: 600
          }}>
            {project.icon || '📦'}
          </div>
        </div>

        {/* Status Badge e Menu de ações */}
        <Space size={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {(!hasSyncs) && (
            <Tooltip 
              title="Este projeto ainda não possui integrações ERP configuradas. Clique em 'Configurações' para conectar um ERP."
              placement="top"
            >
              <InfoCircleOutlined 
                  style={{ 
                    fontSize: 14, 
                    cursor: 'help',
                    color: theme.black
                  }} 
                />
              </Tooltip>
            )}
            <Tag 
              color={hasSyncs ? 'success' : 'warning'}
              style={{ 
                margin: 0,
                borderRadius: 12,
                padding: '2px 12px',
                fontSize: 12,
                fontWeight: 500,
                border: 'none'
              }}
            >
              {hasSyncs ? 'Ativo' : 'Em Configuração'}
            </Tag>
            
            
          </div>

          {/* Menu de ações */}
          {actionItems.length > 0 && can(FEATURE.PROJECT, [ACTION.EDIT]) && (
            <Dropdown
              menu={{ items: actionItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <MoreOutlined style={{ 
                fontSize: 20, 
                cursor: 'pointer',
                color: theme.textSecondary
              }} />
            </Dropdown>
          )}
        </Space>
      </div>

      {/* Título */}
      <h3 style={{
        margin: 0,
        marginBottom: 4,
        fontSize: 16,
        fontWeight: 600,
        color: theme.textSecondary
      }}>
        {project.name}
      </h3>
      
      {/* Subtítulo/Descrição */}
      <p style={{
        margin: 0,
        fontSize: 13,
        color: theme.textSecondary,
        marginBottom: 16
      }}>
        {project.description}
      </p>

      {/* Informações */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 12,
        color: theme.textSecondary,
        marginBottom: 16
      }}>
        <span>
          {project.syncs?.length || project.connectedErps || 0} Syncs conectados
        </span>
        
      </div>

      {/* Footer com avatares e ação */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 'auto'
      }}
      onClick={() => onAction('view', project)}>
        {/* Avatares dos syncs */}
        <div style={{ display: 'flex' }}>
          {Array.isArray(syncList) && syncList.length > 0 && (
            <>
              {syncList.slice(0, 2).map((sync, index) => {
                // Gerar cor baseada no nome do sync
                const colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];
                const color = colors[index % colors.length];
                const initials = sync.name?.charAt(0).toUpperCase() || 'S';
                
                return (
                  <div
                    key={sync.id || index}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      marginLeft: index > 0 ? -10 : 0,
                      border: `2px solid ${theme.white || '#FFFFFF'}`,
                      zIndex: 2 - index
                    }}
                    title={sync.name || `Sync ${index + 1}`}
                  >
                    {initials}
                  </div>
                );
              })}
              {syncList.length > 2 && (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: theme.backgroundSecondary || '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.textSecondary,
                    fontSize: 11,
                    marginLeft: -10,
                    border: `2px solid ${theme.white || '#FFFFFF'}`,
                    zIndex: 0
                  }}
                  title={`+${syncList.length - 2} syncs`}
                >
                  +{syncList.length - 2}
                </div>
              )}
            </>
          )}
          {(!hasSyncs) && (
            <div
              style={{
                fontSize: 11,
                color: theme.textSecondary,
                fontStyle: 'italic'
              }}
            >
              Nenhum sync configurado
            </div>
          )}
        </div>

        {/* Botão de ação */}
        <button
          onClick={() => onAction('view', project)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.textSecondary
          }}
        >
          <RightOutlined style={{ fontSize: 16 }} />
        </button>
      </div>
    </Card>
  );
};

export default ProjectCard;